import type { VariantProps } from "class-variance-authority";
import { format, isToday, isYesterday } from "date-fns";
import type { TFunction } from "i18next";
import { History } from "lucide-react";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";

import { historyTimelineVariants } from "./history-timeline-variants";
import type { RangePreset } from "./range-preset";
import { PRESET_LABELS } from "./range-preset";
import type { EntityHistoryEntry } from "./types";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/v2/alert";
import { Badge } from "@/components/ui/v2/badge";
import { Button } from "@/components/ui/v2/button";
import EmptyState from "@/components/ui/v2/empty-state";
import { Separator } from "@/components/ui/v2/separator";
import { Skeleton } from "@/components/ui/v2/skeleton";

function formatDateLabel(d: Date, t: TFunction): string {
  if (isToday(d)) return t("common.today");
  if (isYesterday(d)) return t("common.yesterday");
  return format(d, "d MMM yyyy");
}

function groupEntriesByDate(
  entries: EntityHistoryEntry[],
  t: TFunction,
): Array<{
  dateKey: string;
  dateLabel: string;
  entries: EntityHistoryEntry[];
}> {
  const sorted = [...entries].sort(
    (a, b) =>
      new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime(),
  );
  const groups = new Map<
    string,
    { dateLabel: string; entries: EntityHistoryEntry[] }
  >();
  for (const entry of sorted) {
    const d = new Date(entry.occurredAt);
    const dateKey = format(d, "yyyy-MM-dd");
    const dateLabel = formatDateLabel(d, t);
    if (!groups.has(dateKey)) {
      groups.set(dateKey, { dateLabel, entries: [] });
    }
    groups.get(dateKey)!.entries.push(entry);
  }
  return Array.from(groups.entries()).map(
    ([dateKey, { dateLabel, entries }]) => ({
      dateKey,
      dateLabel,
      entries,
    }),
  );
}

interface HistoryTimelineProps extends VariantProps<typeof historyTimelineVariants> {
  /** Hook result — pass useEntityHistory() or a feature-specific equivalent return value */
  useHistoryResult: {
    data?: { pages: { data: EntityHistoryEntry[] }[] };
    isLoading: boolean;
    isFetchingNextPage: boolean;
    isError: boolean;
    error: Error | null;
    fetchNextPage: () => void;
    hasNextPage?: boolean;
  };
  /** Render a single history entry */
  renderEntry: (entry: EntityHistoryEntry, index: number) => React.ReactNode;
  /** i18n namespace prefix for empty/error states (e.g. "admin.segments.history") */
  i18nPrefix: string;
  /** Optional toolbar rendered above the timeline. When omitted, preset buttons are shown. */
  toolbar?: React.ReactNode;
  /** Currently active preset — controlled by the parent (used when toolbar is not provided) */
  preset?: RangePreset;
  /** Callback when the user clicks a different preset button (used when toolbar is not provided) */
  onPresetChange?: (preset: RangePreset) => void;
}

export function HistoryTimeline({
  useHistoryResult: {
    data,
    isLoading,
    isFetchingNextPage,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
  },
  renderEntry,
  i18nPrefix,
  toolbar,
  preset,
  onPresetChange,
  inset,
}: HistoryTimelineProps) {
  const { t } = useTranslation();

  const flatEntries = useMemo(
    () => data?.pages?.flatMap((page) => page.data) ?? [],
    [data],
  );
  const grouped = useMemo(
    () => (flatEntries.length > 0 ? groupEntriesByDate(flatEntries, t) : []),
    [flatEntries, t],
  );

  const defaultToolbar =
    preset != null && onPresetChange != null ? (
      <div className="flex flex-wrap items-center gap-2 pt-6">
        {(Object.keys(PRESET_LABELS) as RangePreset[]).map((key) => (
          <Button
            key={key}
            size="sm"
            variant={preset === key ? "default" : "secondary"}
            onClick={() => onPresetChange(key)}
          >
            {PRESET_LABELS[key]}
          </Button>
        ))}
      </div>
    ) : null;

  if (isError) {
    return (
      <div className={historyTimelineVariants({ inset })}>
        {toolbar ?? defaultToolbar}
        <Alert variant="destructive">
          <AlertTitle>{t(`${i18nPrefix}.errorTitle`)}</AlertTitle>
          <AlertDescription>
            {error?.message ?? t(`${i18nPrefix}.errorDefault`)}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (isLoading && flatEntries.length === 0) {
    return (
      <div className={historyTimelineVariants({ inset })}>
        {toolbar ?? defaultToolbar}
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (grouped.length === 0) {
    return (
      <div className={historyTimelineVariants({ inset })}>
        {toolbar ?? defaultToolbar}
        <EmptyState
          icon={History}
          title={t(`${i18nPrefix}.noEntriesTitle`)}
          description={t(`${i18nPrefix}.noEntriesDescription`)}
        />
      </div>
    );
  }

  return (
    <div className={historyTimelineVariants({ inset })}>
      {toolbar ?? defaultToolbar}
      <InfiniteScroll
        dataLength={flatEntries.length}
        next={fetchNextPage}
        hasMore={hasNextPage ?? false}
        scrollThreshold={0.9}
        scrollableTarget="main-content-container"
        loader={
          hasNextPage && isFetchingNextPage ? (
            <div className="space-y-2 pt-4">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : null
        }
      >
        <div className="space-y-4">
          {grouped.map((group) => (
            <div key={group.dateKey} className="space-y-3">
              <div className="flex items-center gap-3">
                <Separator className="flex-1" />
                <Badge variant="outline">{group.dateLabel}</Badge>
                <Separator className="flex-1" />
              </div>
              <ul className="space-y-2 list-none p-0 m-0">
                {group.entries.map((entry, i) => (
                  <li key={`${entry.occurredAt}-${entry.version}-${i}`}>
                    {renderEntry(entry, i)}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </InfiniteScroll>
    </div>
  );
}
